/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import {
  EuiButton,
  EuiButtonEmpty,
  EuiCodeBlock,
  EuiCopy,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  useGeneratedHtmlId,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { AnyProject } from '@modules/project-api/types'
import type { ProjectID, ProjectType, ProjectTypeWithWorkplaceAI } from '@modules/ui-types/projects'
import type {
  PatchElasticsearchProjectRequest,
  PatchObservabilityProjectRequest,
  PatchSecurityProjectRequest,
} from '@modules/project-user-api/v1/types'
import type {
  PatchElasticsearchProjectRequest as AdminPatchElasticsearchProjectRequest,
  PatchObservabilityProjectRequest as AdminPatchObservabilityProjectRequest,
  PatchSecurityProjectRequest as AdminPatchSecurityProjectRequest,
} from '@modules/project-admin-api/v1/types'

type PatchAnyProjectRequest =
  | PatchElasticsearchProjectRequest
  | PatchObservabilityProjectRequest
  | PatchSecurityProjectRequest
  | AdminPatchElasticsearchProjectRequest
  | AdminPatchObservabilityProjectRequest
  | AdminPatchSecurityProjectRequest

type Props = {
  projectId: ProjectID
  projectType: ProjectType
  selectedProjects: AnyProject[]
  onClose: () => void
}

const ApiRequestModal: React.FunctionComponent<Props> = ({
  projectId,
  projectType,
  selectedProjects,
  onClose,
}) => {
  const modalTitleId = useGeneratedHtmlId({ prefix: 'apiRequestModal' })

  const generateApiRequestContent = () => {
    const requestBody: PatchAnyProjectRequest = {
      linked: {
        projects: selectedProjects.reduce<
          Record<string, { type: ProjectTypeWithWorkplaceAI } | null>
        >((acc, project) => {
          acc[project.id] = { type: project.type }
          return acc
        }, {}),
      },
    }

    const httpMethod = 'PATCH'
    const endpoint = `/api/v1/serverless/projects/${projectType}/${projectId}`
    const headers = ['Authorization: Bearer <YOUR_TOKEN>', 'Content-Type: application/json']
    const body = JSON.stringify(requestBody, null, 2)

    return [`${httpMethod} ${endpoint}`, ...headers, '', body].join('\n')
  }

  const apiRequestContent = generateApiRequestContent()

  return (
    <EuiModal onClose={onClose} aria-labelledby={modalTitleId} data-test-subj='cps-apiRequestModal'>
      <EuiModalHeader>
        <EuiModalHeaderTitle id={modalTitleId}>
          <FormattedMessage id='cps.review.apiRequestTitle' defaultMessage='API request' />
        </EuiModalHeaderTitle>
      </EuiModalHeader>

      <EuiModalBody css={{ minWidth: '46rem', overflow: 'auto' }}>
        <EuiCodeBlock
          language='json'
          isCopyable={true}
          paddingSize='l'
          style={{ maxHeight: '368px', overflowY: 'auto' }}
        >
          {apiRequestContent}
        </EuiCodeBlock>
      </EuiModalBody>

      <EuiModalFooter>
        <EuiButtonEmpty
          onClick={onClose}
          aria-label='Close API request modal'
          data-test-subj='cps-apiRequestModal-closeButton'
        >
          <FormattedMessage id='cps.review.apiRequestClose' defaultMessage='Close' />
        </EuiButtonEmpty>
        <EuiCopy textToCopy={apiRequestContent}>
          {(copy) => (
            <EuiButton
              onClick={copy}
              fill={true}
              iconType='copy'
              data-test-subj='cps-apiRequestModal-copyButton'
            >
              <FormattedMessage id='cps.review.apiRequestCopy' defaultMessage='Copy' />
            </EuiButton>
          )}
        </EuiCopy>
      </EuiModalFooter>
    </EuiModal>
  )
}

export default ApiRequestModal
